import React, { useContext, useEffect, useMemo, useRef, useState, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { usePersistentValueLocal } from '../../hooks/usePersistentState';
import { getPlRevenueView } from '../../api/financials_pl';
import { getSalesPlan, putSalesPlanSection } from '../../api/salesPlan';
import {
  defaultModel, sanitize, yearsOf, closedOf, joinedPlanByLine,
  productPlanOf, addProductCard, delProductCard, setProductCardField,
  addProductRow, delProductRow, setProductRowField,
  setProductRowQtyYear, setProductRowQtyMonth, productRowQtyMonth, productRowQtyYear, productRowPriceAt,
  tradeRowCaMonth, tradeRowCaYear, setTradeRowCaMonth, setTradeRowCaYear,
  tradeRowPurchaseMonth, tradeLag, servicePlanLines,
  TRADE_MODES, applyTradeRule, tradeRowStats, tradeRowHasN1,
  productEntry, convertProductCard, moveProductCard,
} from './atterrissage';
import { parsePasteSeries } from './pasteSeries';
import './ProductPlan.css';

/* Section « Plan produits » (module Ventes & Opérations).

   Une CATÉGORIE de produit par card (ex. montres, mouvements) : son COMPTE
   de CA Finance, et ses PRODUITS (ses modèles) portant prix unitaire, coût
   de revient et volumes. Le volume se compte en UNITÉS du produit — aucun
   « inducteur » à nommer : le produit EST l'inducteur.

   Parcours guidé (retour Olivier 2026-08-12) : l'écran vide énonce les 3 pas ;
   créer une catégorie ouvre ses DEUX champs côte à côte (nom · compte) et pose
   déjà un produit vide — plus rien à chercher. « ＋ Produit » vit dans le
   tableau, aligné sous la colonne Produit.

   Le CA (Σ volumes × prix) s'inscrit dans le compte attribué via la jointure
   `joinedPlanByLine` (partagée avec le Plan des ventes : un seul moteur) ; les
   coûts (Σ volumes × coût de revient) alimentent le coût des produits vendus. */

const MONTHS = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
const nf = new Intl.NumberFormat('de-CH');
const fmt = (n) => nf.format(Math.round(Number(n) || 0)).replace(/’|,/g, "'");
const fmt1 = (n) => {
  const x = Number(n) || 0;
  if (Math.abs(x) < 0.05) return '';
  return (Math.round(x * 10) / 10).toLocaleString('de-CH', { maximumFractionDigits: 1 }).replace(/’|,/g, "'");
};
const cleanNum = (v) => { const x = parseFloat(String(v).replace(/['\s’]/g, '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };

const RoCtx = createContext(false);

/* Modèles proposés par le « ＋ Catégorie » — même gabarit de fiche en 3 lignes
   que le Plan services (inducteur · formule · convient pour) : le choix du
   moteur se fait à UN seul endroit, pas dans un bouton séparé par modèle. */
/* Exportés (2026-09-12) : le Plan services les propose dans son menu « ＋ » et
   héberge les cards produits, le Plan produits n'étant plus une page. */
export const PRODUCT_KINDS = (t) => KINDS(t);
const KINDS = (t) => [
  { id: 'units', kind: undefined, label: t('scenarii:productPlan.kUnits', 'Produits (unités)'),
    ind: t('scenarii:productPlan.kUnitsInd', 'unités vendues'),
    formula: t('scenarii:productPlan.kUnitsF', 'unités × prix unitaire'),
    fit: t('scenarii:productPlan.kUnitsFit', 'produits vendus à l\'unité, avec prix et coût de revient') },
  { id: 'trade', kind: 'trade', label: t('scenarii:productPlan.kTrade', 'Négoce (sans stock)'),
    ind: t('scenarii:productPlan.kTradeInd', 'chiffre d\'affaires en kCHF'),
    formula: t('scenarii:productPlan.kTradeF', 'CA × (1 − marge) → achats, décalés de 0 à 3 mois'),
    fit: t('scenarii:productPlan.kTradeFit', 'matériel, logiciel, licences achetés pour les clients — revendus sans passer par le stock') },
];

/* Icônes d'en-tête de card (12 px, trait fin) : horloge = conditions de
   paiement, registre = compte de chiffre d'affaires. Le libellé complet reste
   en infobulle (title). */
const IcTerms = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const IcAccount = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 5h16v14H4z" />
    <path d="M8 9h8M8 13h5" />
  </svg>
);


/* Cellule numérique éditable : bleu, sans cadre (charte). Vide = rien à voir.
   `onSeries` : un collage multi-valeurs (Excel) remplit les mois suivants. */
function Num({ value, onCommit, onSeries, autoFocus = false }) {
  const readOnly = useContext(RoCtx);
  const [draft, setDraft] = useState(null);
  const [foc, setFoc] = useState(false);
  const shown = draft != null ? draft
    : foc ? String(value == null || Number(value) === 0 ? '' : Number(value))
      : fmt1(value);
  if (readOnly) return <span className="spl-ro">{shown || '–'}</span>;
  return (
    <input
      className="spl-num" inputMode="decimal" placeholder="–" value={shown} autoFocus={autoFocus}
      onFocus={() => setFoc(true)}
      onChange={(e) => setDraft(e.target.value)}
      onPaste={onSeries ? (e) => {
        const vals = parsePasteSeries(e);
        if (vals) { e.preventDefault(); onSeries(vals); setDraft(null); }
      } : undefined}
      onBlur={() => { setFoc(false); if (draft == null) return; onCommit(cleanNum(draft)); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

/* HÉBERGÉ (2026-09-12) : passé `host` = { model, update, revLines, view }, le
   composant ne rend que ses cards produits, dans le modèle du Plan services
   qui l'accueille ; il ne charge, ne sauvegarde et n'exporte rien lui-même. */
export default function ProductPlanPage({ activeTenant, addToast, canRead = true, canWrite = true, embedded = false, host = null }) {
  const dragRef = useRef(null);
  const [dropId, setDropId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const { t } = useTranslation();
  const tid = activeTenant?.id;
  const y0 = new Date().getFullYear();
  const [ownModel, setModel] = usePersistentValueLocal(`atterrissage_${tid || 'none'}`, defaultModel(y0));
  const [ownRevLines, setRevLines] = useState(null);
  const [ownView, setView] = useState('m0');
  const model = host ? host.model : ownModel;
  const revLines = host ? host.revLines : ownRevLines;
  const view = host ? host.view : ownView;
  const [fresh, setFresh] = useState(null);       // card à peine créée → focus
  const [menuOpen, setMenuOpen] = useState(false);
  const [rule, setRule] = useState(null);         // popover de règle ouvert (card + ligne)
  const [aggs, setAggs] = useState(new Set());   // cards agrégées (totaux seuls)
  const syncedFor = useRef(null);
  const serverHadContent = useRef(false);
  const saveTimer = useRef(null);

  const cur = model.currentYear || y0;
  const years = yearsOf(model);

  // Le serveur fait foi — le document est PARTAGÉ avec le Plan des ventes.
  useEffect(() => {
    let alive = true;
    if (!tid || host) return () => {};
    syncedFor.current = null;
    getSalesPlan(tid)
      .then((r) => {
        if (!alive) return;
        const remote = r?.error ? null : r?.data?.data;
        if (remote && (Object.keys(remote.data || {}).length || (remote.productPlan?.cards || []).length)) {
          setModel(sanitize(remote, y0));
        }
        // `unwrap` ne rejette JAMAIS (contrat {data,error}) : sans ce test, une
        // lecture en échec armait quand même la sauvegarde et le modèle local
        // (souvent vide) écrasait le plan du serveur — cause des cards
        // disparues (2026-08-16). Lecture KO → on n'arme pas.
        if (!r?.error) {
          serverHadContent.current = !!(remote && (Object.keys(remote.data || {}).length
            || (remote.productPlan?.cards || []).length || (remote.servicePlan?.cards || []).length));
          syncedFor.current = tid;
        }
      })
      // Échec de lecture serveur → on N'ARME PAS la sauvegarde : sinon la page
      // repousserait un modèle local périmé par-dessus la copie serveur.
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid]);

  useEffect(() => {
    let alive = true;
    if (!tid || host) return () => {};
    getPlRevenueView(tid, cur).then((res) => {
      if (alive && !res.error) setRevLines((res.data && res.data.lines) || []);
    }).catch(() => { if (alive) setRevLines([]); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [tid, cur]);

  useEffect(() => {
    if (!tid || host || syncedFor.current !== tid || !canWrite) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Garde-fou : un modèle VIDE ne remplace jamais un plan serveur qui avait
      // du contenu (perte constatée le 2026-08-16).
      const empty = !Object.keys(model.data || {}).length
        && !(model.productPlan?.cards || []).length
        && !(model.servicePlan?.cards || []).length;
      if (empty && serverHadContent.current) return;
      putSalesPlanSection(tid, model, ['productPlan'],
        // La jointure n'est recalculée QUE si les lignes de revenus sont
        // chargées : sinon `computed` partait vide et Finance ne voyait plus le
        // plan (règle « % d'une ligne » à 0 — constat 2026-08-16).
        (merged) => ((revLines || []).length
          ? joinedPlanByLine(merged, revLines.map((l) => l.id))
          : merged.computed)).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [model, tid, revLines]);

  const update = (fn) => {
    if (!canWrite) return;
    if (host) { host.update(fn); return; }
    setModel((prev) => { const d = JSON.parse(JSON.stringify(prev)); fn(d); return d; });
  };

  // Fermeture du popover de règle au clic extérieur.
  useEffect(() => {
    if (!rule) return undefined;
    const close = (e) => { if (!e.target.closest('.spl-pop, .spl-rule')) setRule(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [rule]);

  const MODE_LABELS = {
    manual: t('scenarii:productPlan.mManual', 'Manuel'),
    last: t('scenarii:productPlan.mLast', 'Dernier mois'),
    avg: t('scenarii:productPlan.mAvg', 'Moyenne YTD'),
    target: t('scenarii:productPlan.mTarget', 'Objectif annuel'),
    n1: t('scenarii:productPlan.mN1', 'Saisonnalité N-1'),
    pct: t('scenarii:productPlan.mPct', "% d'une ligne"),
  };
  const modeLabel = (m) => MODE_LABELS[m] || MODE_LABELS.manual;
  const ruleParam = (r) => {
    if (r.mode === 'target') return r.target ? `${fmt(r.target)} kCHF` : null;
    if (r.mode === 'pct') return r.pctRate ? `${fmt1(r.pctRate)} %` : null;
    return null;
  };
  // Créer une catégorie POSE DÉJÀ un produit vide : le parcours ne demande plus
  // de trouver un second bouton.
  const createCategory = (k) => {
    setMenuOpen(false);
    update((d) => {
      const c = addProductCard(d, k && k.kind);
      addProductRow(d, c.id);
      setFresh(c.id);
    });
  };
  const modelMenu = (
    <div className="sp-kinds">
      {KINDS(t).map((k) => (
        <button key={k.id} className="sp-kind" onClick={() => createCategory(k)}>
          <b>{k.label}</b>
          <span className="sp-kind__ind">{t('scenarii:servicePlan.driver', 'inducteur')} : {k.ind}</span>
          <span className="sp-kind__f">{t('scenarii:servicePlan.formula', 'formule')} : {k.formula}</span>
          <i className="sp-kind__fit">{t('scenarii:servicePlan.fitFor', 'convient pour')} : {k.fit}</i>
        </button>
      ))}
    </div>
  );

  /* Export Excel — section SANS onglet : le classeur est bâti à partir des
     tableaux DÉJÀ rendus (`exportRef` de PageShell). On ne remonte pas la page
     hors écran : elle enregistre au fil de la frappe, un second montage serait
     un risque d'écriture. */
  const exportRef = useRef(null);
  const excel = useViewExcelExport({
    ref: exportRef,
    sheetName: t('scenarii:productPlan.title', 'Plan produits'),
    fileBase: t('scenarii:productPlan.title', 'Plan produits'),
    tenantName: activeTenant?.name,
    year: cur,
    addToast,
  });
  const exportOptions = useMemo(
    () => [{ id: 'excel', label: 'Excel', run: excel.run }],
    [excel.run],
  );

  useRegisterPageExports(exportOptions, !embedded && !host);   // embarqué ou hébergé, c'est l'hôte qui exporte (2026-09-12)

  if (!canRead && host) return null;
  if (!canRead) {
    return (
      <PageShell timeless embedded={embedded} className="page-shell--flush" title={t('scenarii:productPlan.title', 'Plan produits')}>
        <div className="spl-empty">{t('scenarii:atterrissage.noAccess', "Vous n'avez pas accès au périmètre Ventes.")}</div>
      </PageShell>
    );
  }

  const isMonthly = view !== 'annual';
  const focusYear = view === 'm1' ? cur + 1 : cur;
  const closed = isMonthly && focusYear === cur ? closedOf(model, cur) : 0;
  // Le compte attribué est une LIGNE DE REPORTING DU CHIFFRE D'AFFAIRES : la
  // vue partagée renvoie toute la structure, on ne garde que la section revenus.
  const revAccounts = (revLines || []).filter((l) => l.section === 'revenue' && !l.hidden);
  const cards = productPlanOf(model);
  /* Colonne de mois : filet à la clôture + aplat ardoise sur la zone
     PRÉVISIONNELLE (mois ≥ clôture) — la vue pluriannuelle n'a pas de
     frontière (une colonne = une année entière). */
  const splitCols = isMonthly && closed > 0 && closed < 12;
  const colCls = (c, base = 'spl-c-val') =>
    `${base}${splitCols && c === closed ? ' spl-bound' : ''}`;
  /* Bandeau au-dessus des mois : l'inducteur à gauche sur les mois RÉELS,
     « Prévisionnel » sur les mois ouverts — ceux qui alimentent le compte de
     résultat. La colonne total reste neutre. */
  const indBand = (label, cols) => (splitCols ? (
    <>
      <th className="spl-band spl-band--real" colSpan={closed}>{t('scenarii:productPlan.realBand', 'Réalisé')}</th>
      <th className="spl-band spl-band--fore" colSpan={12 - closed}>{t('scenarii:productPlan.forecastBand', 'Prévisionnel')}</th>
      <th className="spl-ind" />
    </>
  ) : (
    <th className="spl-ind" colSpan={cols.length + (isMonthly ? 1 : 0)}>{label}</th>
  ));

  /* Card NÉGOCE (kind 'trade') — même grille de colonnes que les autres cards
     (mois alignés), mais lignes en kCHF de CA : mode (manuel / % prestations),
     marge de négoce, décalage d'achat M−0..M−3. Les achats partent directement
     sur la ligne Achats du compte de résultat (computed.tradePurchases). */
  const renderTradeCard = (card) => {
    const rows = card.rows || [];
    const agg = aggs.has(card.id);
    const cols = isMonthly ? MONTHS.map((_, m) => m) : years;
    const prestaLines = servicePlanLines(model)
      .map((id) => revAccounts.find((l) => l.id === id)).filter(Boolean);
    const ruleRow = rule && rule.cardId === card.id ? rows.find((x) => x.id === rule.rowId) : null;
    // Appliquer une règle ÉCRIT les mois ouverts — puis ils restent éditables.
    const applyRule = (r, patch) => update((d) => applyTradeRule(d, card.id, r.id, focusYear, closed, {
      mode: patch.mode != null ? patch.mode : (r.mode || 'manual'),
      target: patch.target != null ? patch.target : r.target,
      rate: patch.rate != null ? patch.rate : r.pctRate,
      lineId: patch.lineId != null ? patch.lineId : r.pctLineId,
    }));
    const caCell = (r, c) => (isMonthly ? tradeRowCaMonth(model, card, r, focusYear, c) : tradeRowCaYear(model, card, r, c));
    const caAt = (c) => rows.reduce((s, r) => s + caCell(r, c), 0);
    const purAt = (c) => (isMonthly
      ? rows.reduce((s, r) => s + tradeRowPurchaseMonth(model, card, r, focusYear, c), 0)
      : MONTHS.reduce((s, _, m) => s + rows.reduce((t, r) => t + tradeRowPurchaseMonth(model, card, r, c, m), 0), 0));
    const margeAt = (c) => rows.reduce((s, r) => s + caCell(r, c) * (Number(r.margin) || 0) / 100, 0);
    const caY = () => cols.reduce((s, c) => s + caAt(c), 0);
    const margeY = cols.reduce((s, c) => s + margeAt(c), 0);
    const pct = caY() ? `${(Math.round(1000 * margeY / caY()) / 10).toLocaleString('de-CH')} %` : '';

    return (
      <section
        className={`spl-card${dropId === card.id ? ' is-dropover' : ''}`} key={card.id}
        onDragOver={(e) => { if (dragRef.current && dragRef.current !== card.id) { e.preventDefault(); if (dropId !== card.id) setDropId(card.id); } }}
        onDragLeave={() => { if (dropId === card.id) setDropId(null); }}
        onDrop={(e) => {
          e.preventDefault();
          const src = dragRef.current;
          if (src && src !== card.id) update((d) => moveProductCard(d, src, card.id));
          dragRef.current = null; setDropId(null);
        }}
      >
        <header className="spl-head">
          {/* Même en-tête que le Plan services (2026-08-16) : ligne 1 = poignée,
              pastille, titre, badge du modèle ; ligne 2 = réglages étiquetés. */}
          <div className="spl-head__row">
            {canWrite && (
              <span
                className="spl-grip" title={t('scenarii:servicePlan.dragCard', 'Glisser pour réorganiser')}
                draggable
                onDragStart={(e) => { dragRef.current = card.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', card.id); }}
                onDragEnd={() => { dragRef.current = null; setDropId(null); }}
              >⋮⋮</span>
            )}
            <button
              type="button" className="spl-mark"
              title={agg ? t('scenarii:productPlan.expand', 'Détailler les produits') : t('scenarii:productPlan.collapse', 'Agréger la catégorie')}
              onClick={() => setAggs((s0) => { const n = new Set(s0); if (n.has(card.id)) n.delete(card.id); else n.add(card.id); return n; })}
            >
              <svg className={`spl-mark__logo${agg ? '' : ' is-open'}`} viewBox="0 0 64 64" width="16" height="16" aria-hidden>
                <g fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round">
                  <path d="M25 52 h-7 a6 6 0 0 1 -6 -6 v-28 a6 6 0 0 1 6 -6 h28 a6 6 0 0 1 6 6 v28 a6 6 0 0 1 -6 6 h-7" />
                  <rect className="spl-mark__court" x="24" y="24" width="16" height="16" rx="3" fill="currentColor" stroke="none" />
                </g>
              </svg>
            </button>
            <input
              className="spl-catname" value={card.name || ''} disabled={!canWrite}
              autoFocus={fresh === card.id}
              placeholder={t('scenarii:productPlan.tradePh', 'ex. matériel clients')}
              onChange={(e) => update((d) => setProductCardField(d, card.id, 'name', e.target.value))} />
            {agg && (
              <span className="spl-aggsum">
                <span className="spl-spark" aria-hidden>
                  {(isMonthly ? cols : MONTHS.map((_, m) => m)).slice(0, 12).map((c) => {
                    const vals = (isMonthly ? cols : MONTHS.map((_, m) => m)).map((cc) => caAt(cc));
                    const mx = Math.max(...vals, 0.0001);
                    return <i key={c} style={{ height: `${Math.max(2, Math.round(18 * caAt(c) / mx))}px` }} />;
                  })}
                </span>
                {pct && <span className="spl-aggpct">{pct}</span>}
                <span className="spl-aggtot">{fmt(caY())}<small>kCHF</small></span>
              </span>
            )}
            <span className="sp-kindtag spl-head__tag">{t('scenarii:productPlan.kTrade', 'Négoce (sans stock)')}</span>
            {canWrite && (
              <button className="spl-cardx" tabIndex={-1} title={t('scenarii:productPlan.delCard', 'Supprimer la catégorie')}
                onClick={() => setConfirmDel(card)}>
                <span aria-hidden>✕</span>
              </button>
            )}
          </div>
          {!agg && (
            <div className="spl-head__cfg">
              <span className="spl-cfg">
                <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                <span className="spl-pillseg" role="group">
                  <button type="button" className="on" disabled>{t('scenarii:productPlan.uRev', "Chiffre d'affaires")}</button>
                </span>
              </span>
              <span className="spl-cfg">
                <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                <span className="spl-pillseg" role="group">
                  <button type="button" className="on" disabled>{t('scenarii:servicePlan.eKchf', 'kCHF')}</button>
                </span>
              </span>
              <span className="spl-cfg">
                <label>{t('scenarii:productPlan.accountShort', 'Compte')}</label>
                <select
                  className={`spl-acct${card.lineId ? '' : ' spl-acct--todo'}`}
                  value={card.lineId || ''} disabled={!canWrite}
                  onChange={(e) => update((d) => setProductCardField(d, card.id, 'lineId', e.target.value))}>
                  <option value="">{t('scenarii:productPlan.pickLine', 'à choisir…')}</option>
                  {revAccounts.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                <select
                  className="spl-acct" value={Number(card.terms) || 30} disabled={!canWrite}
                  onChange={(e) => update((d) => setProductCardField(d, card.id, 'terms', Number(e.target.value)))}>
                  {[0, 30, 45, 60, 90].map((j) => (
                    <option key={j} value={j}>{j === 0 ? t('scenarii:servicePlan.cash', 'comptant') : `à ${j} jours`}</option>
                  ))}
                </select>
              </span>
            </div>
          )}
        </header>

        {!agg && (
        <div className="spl-scroll lk-hscroll">
          <table className="spl-table spl-table--fixed">
            <colgroup>
              <col style={{ width: 190 }} />
              <col className="spl-col-price" />
              <col className="spl-col-price" />
              <col className="spl-col-cost" />
              <col style={{ width: 10 }} />
              {cols.map((c) => <col key={c} />)}
              {isMonthly && <col style={{ width: 84 }} />}
            </colgroup>
            <thead>
              <tr className="spl-indrow">
                <th className="spl-c-lbl" />
                <th className="spl-c-unit" /><th className="spl-c-unit" /><th className="spl-c-unit" />
                <th className="spl-c-sep" />
                {indBand(t('scenarii:productPlan.tradeCa', 'Chiffre d\'affaires planifié (kCHF)'), cols)}
              </tr>
              <tr>
                <th className="spl-c-lbl">{t('scenarii:productPlan.tradeFamily', 'Famille')}</th>
                <th className="spl-c-rule">{t('scenarii:productPlan.tradeRule', 'Règle')}</th>
                <th className="spl-c-unit" title={t('scenarii:productPlan.tradeMarginHint', 'Marge de négoce, en % du prix de vente')}>{t('scenarii:productPlan.tradeMargin', 'Marge %')}</th>
                <th className="spl-c-unit" title={t('scenarii:productPlan.tradeLagHint', 'Achat comptabilisé n mois avant la vente')}>{t('scenarii:productPlan.tradeLag', 'Achat')}</th>
                <th className="spl-c-sep" />
                {cols.map((c) => (
                  <th key={c} className={colCls(c)}>
                    {isMonthly ? MONTHS[c] : c}
                  </th>
                ))}
                {isMonthly && <th className="spl-c-tot">{focusYear}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td className="spl-c-lbl">
                      <input
                        className="spl-rowname" value={r.name || ''} disabled={!canWrite}
                        autoFocus={fresh === card.id && ri === 0 && !!card.name}
                        placeholder={t('scenarii:productPlan.tradeRowPh', 'ex. licences')}
                        onChange={(e) => update((d) => setProductRowField(d, card.id, r.id, 'name', e.target.value))} />
                      {canWrite && (
                        <button className="spl-rowx" tabIndex={-1} title={t('scenarii:productPlan.delRow', 'Supprimer le produit')}
                          onClick={() => update((d) => delProductRow(d, card.id, r.id))}>✕</button>
                      )}
                    </td>
                    <td className="spl-c-rule">
                      <button
                        type="button" className="spl-rule" disabled={!canWrite}
                        title={t('scenarii:productPlan.ruleHint', 'Règle de calcul des mois prévisionnels — elle écrit une base, ajustable ensuite mois par mois')}
                        onClick={(e) => {
                          const card0 = e.currentTarget.closest('.spl-card');
                          const a = e.currentTarget.getBoundingClientRect();
                          const b = card0.getBoundingClientRect();
                          setRule({ cardId: card.id, rowId: r.id, x: Math.round(a.left - b.left), y: Math.round(a.bottom - b.top + 4) });
                        }}>
                        <span>{modeLabel(r.mode)} <i aria-hidden>▾</i></span>
                        {ruleParam(r) && <em>{ruleParam(r)}</em>}
                      </button>
                    </td>
                    <td className="spl-c-unit"><Num value={r.margin} onCommit={(v) => update((d) => setProductRowField(d, card.id, r.id, 'margin', v))} /></td>
                    <td className="spl-c-unit">
                      <select
                        className="spl-cellsel" value={tradeLag(r)} disabled={!canWrite}
                        onChange={(e) => update((d) => setProductRowField(d, card.id, r.id, 'lag', Number(e.target.value)))}>
                        {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n ? `M−${n}` : 'M0'}</option>)}
                      </select>
                    </td>
                    <td className="spl-c-sep" />
                    {cols.map((c) => (
                      /* Toujours éditable, même après application d'une règle :
                         la règle pose une BASE, le vendeur ajuste. */
                      <td key={c} className={colCls(c)}>
                        <Num
                          value={caCell(r, c)}
                          onCommit={(v) => update((d) => (isMonthly
                            ? setTradeRowCaMonth(d, card.id, r.id, focusYear, c, v)
                            : setTradeRowCaYear(d, card.id, r.id, c, v)))}
                          onSeries={isMonthly ? (vals) => update((d) => vals.forEach((v, i) => {
                            if (c + i < 12) setTradeRowCaMonth(d, card.id, r.id, focusYear, c + i, v);
                          })) : undefined} />
                      </td>
                    ))}
                    {isMonthly && <td className="spl-c-tot">{fmt1(tradeRowCaYear(model, card, r, focusYear)) || '–'}</td>}
                  </tr>
                </React.Fragment>
              ))}
              {canWrite && (
                <tr className="spl-addrow">
                  <td className="spl-c-lbl">
                    <button onClick={() => update((d) => addProductRow(d, card.id))}>
                      ＋ {t('scenarii:productPlan.tradeAddRow', 'Famille')}
                    </button>
                  </td>
                  <td className="spl-c-unit" />
                  <td className="spl-c-unit" />
                  <td className="spl-c-unit" />
                  <td className="spl-c-sep" />
                  {cols.map((c) => <td key={c} className={colCls(c)} />)}
                  {isMonthly && <td className="spl-c-tot" />}
                </tr>
              )}
              <tr className="spl-sum spl-sum--ca">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.revenue', 'Chiffre d\'affaires')}
                  <span className="spl-unit">kCHF</span>
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(caAt(c)) === '0' ? '–' : fmt(caAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(caY()) === '0' ? '–' : fmt(caY())}</td>}
              </tr>
              <tr className="spl-sum">
                <td className="spl-c-lbl" title={t('scenarii:productPlan.tradePurchasesHint', 'Achats comptabilisés avec leur décalage — alimentent la ligne Achats du compte de résultat')}>
                  {t('scenarii:productPlan.tradePurchases', 'Achats')}
                  <span className="spl-unit">kCHF</span>
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(purAt(c)) === '0' ? '–' : fmt(purAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(cols.reduce((s, c) => s + purAt(c), 0)) === '0' ? '–' : fmt(cols.reduce((s, c) => s + purAt(c), 0))}</td>}
              </tr>
              <tr className="spl-sum spl-sum--marge">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.margin', 'Marge')}
                  {pct && <span className="spl-pct">{pct}</span>}
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(margeAt(c)) === '0' ? '–' : fmt(margeAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(margeY) === '0' ? '–' : fmt(margeY)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
        )}
        {/* Popover de règle — POSÉ HORS de la zone défilante (sinon coupé), à
            l'aplomb de la pastille cliquée. Les indicateurs d'abord : on décide
            en voyant la moyenne, le dernier mois et le manco. */}
        {ruleRow && (() => {
          const st = tradeRowStats(model, card, ruleRow, focusYear, closed);
          const hasN1 = tradeRowHasN1(model, card, ruleRow, focusYear);
          const dis = { last: !closed, avg: !closed, n1: !hasN1 };
          const ind = (lbl, v, suffix) => (
            <div className="spl-pop__i"><span>{lbl}</span><b>{v}{suffix}</b></div>
          );
          return (
            <div className="spl-pop" style={{ left: rule.x, top: rule.y }}>
              <div className="spl-pop__h">{t('scenarii:productPlan.ruleTitle', 'Règle de calcul')}</div>
              <div className="spl-pop__inds">
                {ind(t('scenarii:productPlan.iYtd', 'Réel cumulé'), fmt(st.ytdSum), ' kCHF')}
                {ind(t('scenarii:productPlan.iAvg', 'Moyenne YTD'), fmt1(st.ytdAvg) || '0', ' kCHF')}
                {ind(t('scenarii:productPlan.iLast', 'Dernier mois'), fmt1(st.last) || '0', ' kCHF')}
                {ind(t('scenarii:productPlan.iYear', 'Année planifiée'), fmt(st.total), ' kCHF')}
                {!!st.target && ind(t('scenarii:productPlan.iGap', 'Manco / objectif'), fmt(st.gap), ' kCHF')}
              </div>
              <div className="spl-pop__modes">
                {TRADE_MODES.map((m) => (
                  <button
                    key={m} type="button" disabled={!canWrite || dis[m]}
                    className={ruleRow.mode === m || (!ruleRow.mode && m === 'manual') ? 'on' : ''}
                    title={dis[m] ? (m === 'n1'
                      ? t('scenarii:productPlan.noN1', 'Aucune donnée sur l\'année précédente')
                      : t('scenarii:productPlan.noClosed', 'Aucun mois clôturé')) : undefined}
                    onClick={() => applyRule(ruleRow, { mode: m })}>
                    {modeLabel(m)}
                  </button>
                ))}
              </div>
              {ruleRow.mode === 'target' && (
                <label className="spl-pop__p">
                  {t('scenarii:productPlan.pTarget', 'Objectif {{year}}', { year: focusYear })}
                  <Num value={ruleRow.target} onCommit={(v) => applyRule(ruleRow, { mode: 'target', target: v })} />
                  <span>kCHF</span>
                </label>
              )}
              {ruleRow.mode === 'pct' && (
                <>
                  <label className="spl-pop__p">
                    {t('scenarii:productPlan.tradePctBase', 'Base')}
                    <select
                      className="spl-cellsel" value={ruleRow.pctLineId || ''} disabled={!canWrite}
                      onChange={(e) => applyRule(ruleRow, { mode: 'pct', lineId: e.target.value })}>
                      <option value="">{t('scenarii:productPlan.pickLine', 'à choisir…')}</option>
                      {prestaLines.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </label>
                  <label className="spl-pop__p">
                    {t('scenarii:productPlan.tradePctRate', 'Taux')}
                    <Num value={ruleRow.pctRate} onCommit={(v) => applyRule(ruleRow, { mode: 'pct', rate: v })} />
                    <span>%</span>
                  </label>
                </>
              )}
              <p className="spl-pop__n">{t('scenarii:productPlan.ruleNote', 'La règle remplit les mois prévisionnels — ils restent modifiables.')}</p>
            </div>
          );
        })()}
      </section>
    );
  };

  const renderCard = (card) => {
    if (card.kind === 'trade') return renderTradeCard(card);
    const rows = card.rows || [];
    const agg = aggs.has(card.id);
    const qty = (r, c) => (isMonthly ? productRowQtyMonth(r, focusYear, c) : productRowQtyYear(r, c));
    const pdE = productEntry(card);
    const cols = isMonthly ? MONTHS.map((_, m) => m) : years;
    const sum = (fn) => rows.reduce((s, r) => s + fn(r), 0);
    const volAt = (c) => sum((r) => qty(r, c));
    const caAt = (c) => sum((r) => qty(r, c) * productRowPriceAt(model, r, isMonthly ? focusYear : c) / 1000);
    const crAt = (c) => sum((r) => qty(r, c) * (Number(r.cost) || 0) / 1000);
    const volY = () => sum((r) => productRowQtyYear(r, focusYear));
    const caY = () => cols.reduce((s, c) => s + caAt(c), 0);
    const crY = () => cols.reduce((s, c) => s + crAt(c), 0);
    const margeY = caY() - crY();
    const pct = caY() ? `${(Math.round(1000 * margeY / caY()) / 10).toLocaleString('de-CH')} %` : '';

    return (
      <section
        className={`spl-card${dropId === card.id ? ' is-dropover' : ''}`} key={card.id}
        onDragOver={(e) => { if (dragRef.current && dragRef.current !== card.id) { e.preventDefault(); if (dropId !== card.id) setDropId(card.id); } }}
        onDragLeave={() => { if (dropId === card.id) setDropId(null); }}
        onDrop={(e) => {
          e.preventDefault();
          const src = dragRef.current;
          if (src && src !== card.id) update((d) => moveProductCard(d, src, card.id));
          dragRef.current = null; setDropId(null);
        }}
      >
        {/* Les DEUX champs de la catégorie, côte à côte et étiquetés : ce qu'il
            faut renseigner se voit d'un coup d'œil. */}
        <header className="spl-head">
          {/* Même en-tête que le Plan services (2026-08-16) : ligne 1 = poignée,
              pastille, titre, badge du modèle ; ligne 2 = réglages étiquetés. */}
          <div className="spl-head__row">
            {canWrite && (
              <span
                className="spl-grip" title={t('scenarii:servicePlan.dragCard', 'Glisser pour réorganiser')}
                draggable
                onDragStart={(e) => { dragRef.current = card.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', card.id); }}
                onDragEnd={() => { dragRef.current = null; setDropId(null); }}
              >⋮⋮</span>
            )}
            <button
              type="button" className="spl-mark"
              title={agg ? t('scenarii:productPlan.expand', 'Détailler les produits') : t('scenarii:productPlan.collapse', 'Agréger la catégorie')}
              onClick={() => setAggs((s0) => { const n = new Set(s0); if (n.has(card.id)) n.delete(card.id); else n.add(card.id); return n; })}
            >
              <svg className={`spl-mark__logo${agg ? '' : ' is-open'}`} viewBox="0 0 64 64" width="16" height="16" aria-hidden>
                <g fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round">
                  <path d="M25 52 h-7 a6 6 0 0 1 -6 -6 v-28 a6 6 0 0 1 6 -6 h28 a6 6 0 0 1 6 6 v28 a6 6 0 0 1 -6 6 h-7" />
                  <rect className="spl-mark__court" x="24" y="24" width="16" height="16" rx="3" fill="currentColor" stroke="none" />
                </g>
              </svg>
            </button>
            <input
              className="spl-catname" value={card.name || ''} disabled={!canWrite}
              autoFocus={fresh === card.id}
              placeholder={t('scenarii:productPlan.categoryPh', 'ex. montres')}
              onChange={(e) => update((d) => setProductCardField(d, card.id, 'name', e.target.value))} />
            {agg && (
              <span className="spl-aggsum">
                <span className="spl-spark" aria-hidden>
                  {(isMonthly ? cols : MONTHS.map((_, m) => m)).slice(0, 12).map((c) => {
                    const vals = (isMonthly ? cols : MONTHS.map((_, m) => m)).map((cc) => caAt(cc));
                    const mx = Math.max(...vals, 0.0001);
                    return <i key={c} style={{ height: `${Math.max(2, Math.round(18 * caAt(c) / mx))}px` }} />;
                  })}
                </span>
                {pct && <span className="spl-aggpct">{pct}</span>}
                <span className="spl-aggtot">{fmt(caY())}<small>kCHF</small></span>
              </span>
            )}
            <span className="sp-kindtag spl-head__tag">{t('scenarii:productPlan.kUnits', 'Produits (unités)')}</span>
            {canWrite && (
              <button className="spl-cardx" tabIndex={-1} title={t('scenarii:productPlan.delCard', 'Supprimer la catégorie')}
                onClick={() => setConfirmDel(card)}>
                <span aria-hidden>✕</span>
              </button>
            )}
          </div>
          {!agg && (
            <div className="spl-head__cfg">
              <span className="spl-cfg">
                <label>{t('scenarii:servicePlan.unitLbl', 'Unité')}</label>
                <span className="spl-pillseg" role="group">
                  <button type="button" className="on" disabled>{t('scenarii:productPlan.uUnits', 'Unités')}</button>
                </span>
              </span>
              <span className="spl-cfg">
                <label>{t('scenarii:servicePlan.entryMode', 'Saisie')}</label>
                <span className="spl-pillseg" role="group">
                  {[['qty', t('scenarii:servicePlan.hbQty2', 'Qté')],
                    ['chf', t('scenarii:servicePlan.eKchf', 'kCHF')]].map(([e, lbl]) => (
                    <button
                      key={e} type="button" disabled={!canWrite}
                      className={productEntry(card) === e ? 'on' : ''}
                      onClick={() => update((d) => convertProductCard(d, card.id, e))}>
                      {lbl}
                    </button>
                  ))}
                </span>
              </span>
              <span className="spl-cfg">
                <label>{t('scenarii:productPlan.accountShort', 'Compte')}</label>
                <select
                  className={`spl-acct${card.lineId ? '' : ' spl-acct--todo'}`}
                  value={card.lineId || ''} disabled={!canWrite}
                  onChange={(e) => update((d) => setProductCardField(d, card.id, 'lineId', e.target.value))}>
                  <option value="">{t('scenarii:productPlan.pickLine', 'à choisir…')}</option>
                  {revAccounts.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
                <select
                  className="spl-acct" value={Number(card.terms) || 30} disabled={!canWrite}
                  onChange={(e) => update((d) => setProductCardField(d, card.id, 'terms', Number(e.target.value)))}>
                  {[0, 30, 45, 60, 90].map((j) => (
                    <option key={j} value={j}>{j === 0 ? t('scenarii:servicePlan.cash', 'comptant') : `à ${j} jours`}</option>
                  ))}
                </select>
              </span>
            </div>
          )}
        </header>

        {!agg && (
        <div className="spl-scroll lk-hscroll">
          <table className="spl-table spl-table--fixed">
            <colgroup>
              <col style={{ width: 190 }} />
              <col className="spl-col-price" />
              <col className="spl-col-price" />
              <col className="spl-col-cost" />
              <col style={{ width: 10 }} />
              {cols.map((c) => <col key={c} />)}
              {isMonthly && <col style={{ width: 84 }} />}
            </colgroup>
            <thead>
              {/* L'inducteur mensuel en clair au-dessus des mois. */}
              <tr className="spl-indrow">
                <th className="spl-c-lbl" />
                <th className="spl-c-unit" /><th className="spl-c-unit" /><th className="spl-c-unit" />
                <th className="spl-c-sep" />
                {indBand(t('scenarii:productPlan.unitsSold', 'Unités vendues'), cols)}
              </tr>
              <tr>
                <th className="spl-c-lbl">{t('scenarii:productPlan.product', 'Produit')}</th>
                <th className="spl-c-unit">{t('scenarii:productPlan.unitPrice', 'Prix unitaire')}</th>
                <th className="spl-c-unit" title={t('scenarii:productPlan.idxHint', 'Augmentation annuelle du prix, appliquée dès l\'année suivante')}>{t('scenarii:productPlan.idx', '± % / an')}</th>
                <th className="spl-c-unit" title={t('scenarii:productPlan.unitCost', 'Coût matériel')}>{t('scenarii:productPlan.unitCost', 'Coût matériel')}</th>
                <th className="spl-c-sep" />
                {cols.map((c) => (
                  <th key={c} className={colCls(c)}>
                    {isMonthly ? MONTHS[c] : c}
                  </th>
                ))}
                {isMonthly && <th className="spl-c-tot">{focusYear}</th>}
              </tr>
            </thead>
            <tbody>
              {!agg && rows.map((r, ri) => (
                <tr key={r.id}>
                  <td className="spl-c-lbl">
                    <input
                      className="spl-rowname" value={r.name || ''} disabled={!canWrite}
                      autoFocus={fresh === card.id && ri === 0 && !!card.name}
                      placeholder={t('scenarii:productPlan.productPh', 'ex. modèle')}
                      onChange={(e) => update((d) => setProductRowField(d, card.id, r.id, 'name', e.target.value))} />
                    {canWrite && (
                      <button className="spl-rowx" tabIndex={-1} title={t('scenarii:productPlan.delRow', 'Supprimer le produit')}
                        onClick={() => update((d) => delProductRow(d, card.id, r.id))}>✕</button>
                    )}
                  </td>
                  <td className="spl-c-unit"><Num value={r.price} onCommit={(v) => update((d) => setProductRowField(d, card.id, r.id, 'price', Math.round(v)))} /></td>
                  <td className="spl-c-unit"><Num value={r.index} onCommit={(v) => update((d) => setProductRowField(d, card.id, r.id, 'index', v))} /></td>
                  <td className="spl-c-unit"><Num value={r.cost} onCommit={(v) => update((d) => setProductRowField(d, card.id, r.id, 'cost', Math.round(v)))} /></td>
                  <td className="spl-c-sep" />
                  {cols.map((c) => (
                    /* Les mois CLOS restent saisissables : les unités vendues
                       sont connues et servent la lecture d'année complète (le
                       chiffre d'affaires de ces mois vient de la compta —
                       le serveur n'écoute le plan que sur les mois ouverts). */
                    <td key={c} className={colCls(c)}>
                      {(() => {
                        // Mode kCHF : la cellule montre le CA du mois et le
                        // reconvertit en unités (le stock de vérité = unités).
                        const p0 = productRowPriceAt(card, r, isMonthly ? focusYear : c) || 0;
                        const toQty = (v) => (p0 ? (Number(v) || 0) * 1000 / p0 : 0);
                        const inChf = pdE === 'chf';
                        const val = inChf ? (qty(r, c) * p0) / 1000 : qty(r, c);
                        const put = (d, cc, v) => (isMonthly
                          ? setProductRowQtyMonth(d, card.id, r.id, focusYear, cc, inChf ? toQty(v) : v)
                          : setProductRowQtyYear(d, card.id, r.id, cc, inChf ? toQty(v) : v));
                        return (
                          <Num
                            value={val}
                            onCommit={(v) => update((d) => put(d, c, v))}
                            onSeries={isMonthly ? (vals) => update((d) => vals.forEach((v, i) => {
                              if (c + i < 12) put(d, c + i, v);
                            })) : undefined} />
                        );
                      })()}
                    </td>
                  ))}
                  {isMonthly && (
                    <td className="spl-c-tot">
                      {pdE === 'chf'
                        ? fmt(cols.reduce((a, c) => a + qty(r, c) * (productRowPriceAt(card, r, focusYear) || 0) / 1000, 0))
                        : fmt1(productRowQtyYear(r, focusYear)) || '–'}
                    </td>
                  )}
                </tr>
              ))}
              {canWrite && !agg && (
                <tr className="spl-addrow">
                  <td className="spl-c-lbl">
                    <button onClick={() => update((d) => addProductRow(d, card.id))}>
                      ＋ {t('scenarii:productPlan.addRow', 'Produit')}
                    </button>
                  </td>
                  <td className="spl-c-unit" />
                  <td className="spl-c-unit" />
                  <td className="spl-c-unit" />
                  <td className="spl-c-sep" />
                  {cols.map((c) => <td key={c} className={colCls(c)} />)}
                  {isMonthly && <td className="spl-c-tot" />}
                </tr>
              )}
              {!agg && (
              <tr className="spl-sum spl-sum--first">
                <td className="spl-c-lbl">{t('scenarii:productPlan.units', 'Unités vendues')}</td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt1(volAt(c)) || '–'}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt1(volY()) || '–'}</td>}
              </tr>
              )}
              <tr className="spl-sum spl-sum--ca">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.revenue', 'Chiffre d\'affaires')}
                  <span className="spl-unit">kCHF</span>
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(caAt(c)) === '0' ? '–' : fmt(caAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(caY()) === '0' ? '–' : fmt(caY())}</td>}
              </tr>
              {!agg && (
              <tr className="spl-sum">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.cogsMat', 'Coût matériel')}
                  <span className="spl-unit">kCHF</span>
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => <td key={c} className={colCls(c)}>{fmt(crAt(c)) === '0' ? '–' : fmt(crAt(c))}</td>)}
                {isMonthly && <td className="spl-c-tot">{fmt(crY()) === '0' ? '–' : fmt(crY())}</td>}
              </tr>
              )}
              {!agg && (
              <tr className="spl-sum spl-sum--marge">
                <td className="spl-c-lbl">
                  {t('scenarii:productPlan.margin', 'Marge')}
                  {pct && <span className="spl-pct">{pct}</span>}
                </td>
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-unit" />
                <td className="spl-c-sep" />
                {cols.map((c) => {
                  const m = caAt(c) - crAt(c);
                  return <td key={c} className={colCls(c)}>{fmt(m) === '0' ? '–' : fmt(m)}</td>;
                })}
                {isMonthly && <td className="spl-c-tot">{fmt(margeY) === '0' ? '–' : fmt(margeY)}</td>}
              </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </section>
    );
  };

  const dialogueSuppression = (
    confirmDel && (
        <div className="spl-confirm" role="dialog" aria-modal="true">
          <div className="spl-confirm__box">
            <p>{t('scenarii:productPlan.delConfirm', 'Supprimer la carte « {{name}} » et toutes ses lignes ?', { name: confirmDel.name || t('scenarii:productPlan.category', 'catégorie') })}</p>
            <div className="spl-confirm__btns">
              <button type="button" onClick={() => setConfirmDel(null)}>{t('common:cancel', 'Annuler')}</button>
              <button type="button" className="spl-confirm__del"
                onClick={() => { update((d) => delProductCard(d, confirmDel.id)); setConfirmDel(null); }}>
                {t('scenarii:servicePlan.delCardBtn', 'Supprimer')}
              </button>
            </div>
          </div>
        </div>
    )
  );
  if (host) {
    return (
      <RoCtx.Provider value={!canWrite}>
        {cards.map(renderCard)}
        {dialogueSuppression}
      </RoCtx.Provider>
    );
  }
  return (
    <RoCtx.Provider value={!canWrite}>
      <PageShell timeless embedded={embedded} exportRef={exportRef} className="page-shell--flush" title={t('scenarii:productPlan.title', 'Plan produits')}>
        <div className="spl-bar">
          <div className="spl-seg">
            {[['m0', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year: cur })],
              ['m1', t('scenarii:atterrissage.viewMonth', '{{year}} mensuel', { year: cur + 1 })]].map(([k, lbl]) => (
              /* La vue « Plan pluriannuel » a quitté l'Ancienne prévision (2026-09-12) :
                 le pluriannuel vit dans la Trajectoire. */
              <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k)}>{lbl}</button>
            ))}
          </div>
          {canWrite && cards.length > 0 && (
            <span className="sp-addwrap">
              <button className="spl-add spl-add--primary" onClick={() => setMenuOpen((v) => !v)}>
                ＋ {t('scenarii:productPlan.addCard', 'Catégorie')}
              </button>
              {menuOpen && modelMenu}
            </span>
          )}
          {cards.length > 0 && (
            <button className="spl-add spl-add--quiet" onClick={() => setAggs((s0) => (s0.size >= cards.length ? new Set() : new Set(cards.map((c) => c.id))))}>
              {aggs.size >= cards.length ? t('scenarii:servicePlan.expandAll', 'Tout déplier') : t('scenarii:servicePlan.collapseAll', 'Tout replier')}
            </button>
          )}
        </div>

        {!cards.length ? (
          /* Écran vide : le choix du MODÈLE enseigne le parcours (fiches en 3
             lignes), comme au Plan services — plus de liste d'étapes séparée. */
          <section className="spl-start">
            <h2>{t('scenarii:productPlan.startTitle', 'Planifiez vos ventes de produits')}</h2>
            <p className="sp-startp">{t('scenarii:productPlan.startBody', 'Choisis un modèle — chaque card fait le calcul pour toi et alimente le compte de chiffre d\'affaires que tu lui attribues.')}</p>
            {canWrite && modelMenu}
          </section>
        ) : cards.map(renderCard)}
      {dialogueSuppression}
      </PageShell>
    </RoCtx.Provider>
  );
}
